import { PropsWithChildren, createContext, useContext, useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { AD_MANAGEMENT_API } from 'configs/api/adManagement';
import { useHttpClient } from 'hooks';
import { IAdSectionBid, IAdSectionBidCreatePayload, IAdSectionBidEditPayload } from 'types/adManagement';

// ! CONTEXT LOGIC HOOK
const useAdBidLogic = () => {
	const { adBidId, vendorId } = useParams();

	// ! http client
	const fetchHttpClient = useHttpClient<IAdSectionBid>();
	const createHttpClient = useHttpClient();
	const editHttpClient = useHttpClient();

	// ! states
	const [data, setData] = useState<IAdSectionBid>();

	// ! memos

	// ! handlers
	const fetchData = () => {
		if (!vendorId || !adBidId || fetchHttpClient.isLoading) return;

		return fetchHttpClient.request({
			requestConfig: AD_MANAGEMENT_API.AD_BIDS.get(+vendorId, +adBidId),
			successCallback: (response) => {
				setData(response);
			},
		});
	};

	const create = (payload: IAdSectionBidCreatePayload) => {
		if (!vendorId) return;

		return fetchHttpClient.request({
			requestConfig: AD_MANAGEMENT_API.AD_BIDS.create(+vendorId, payload),
			withPromiseReject: true,
		});
	};

	const edit = (adBidId: number, payload: Partial<IAdSectionBidEditPayload>) => {
		if (!vendorId) return;

		return editHttpClient.request({
			requestConfig: AD_MANAGEMENT_API.AD_BIDS.update(+vendorId, adBidId, payload),
			withPromiseReject: true,
		});
	};

	// ! effects
	useEffect(() => {
		fetchData();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [vendorId, adBidId]);

	return {
		data,
		adBidId,
		vendorId: +vendorId!,

		create,
		edit,

		//
		fetchVendorDataHttpClient: fetchHttpClient,
		createVendorDataHttpClient: createHttpClient,
		editVendorDataHttpClient: editHttpClient,
	};
};

// ! CONTEXT
type IAdBidContextData = ReturnType<typeof useAdBidLogic>;
const AdBidContext = createContext<IAdBidContextData | undefined>(undefined);

// ! CONTEXT PROVIDER
export const AdBidProvider = ({ children = <Outlet /> }: PropsWithChildren) => {
	const value = useAdBidLogic();

	return <AdBidContext.Provider value={value}>{children}</AdBidContext.Provider>;
};

// ! CONTEXT CONSUMER HOOK
export const useAdBid = () => {
	const ctx = useContext(AdBidContext);

	if (!ctx) {
		throw new Error('useAdBid must be used inside AdBidProvider');
	}

	return ctx;
};
